/**
 * 55 — the shared "now playing" data store.
 *
 * The footer transport lives outside every screen and never tears down, so it
 * can't reach into the song screen's state. But it wants two things the song
 * screen already has for whatever's playing: the note timestamps (the dots on
 * the footer wave) and the take's synced lyric lines (the strip in the expanded
 * footer).
 *
 * Rather than have the footer re-fetch either — and rather than teach the player
 * about notes and lyrics — the song screen PUBLISHES them here, keyed by version
 * id, whenever it loads them. The footer reads the entry for player.now and
 * repaints. Playing straight off the album (never opening the song) simply has
 * no entry, and the footer shows the wave + transport without dots or words —
 * which is fine, that's the bonus layer.
 *
 * Entries are tiny (a few dozen numbers + short strings) and there are only ever
 * a handful of them, so the map is never pruned.
 */

export interface NowLyricLine {
  text: string;
  start_s: number;
}

export interface NowEntry {
  /** note timestamps on this version, for the dots on the wave */
  marks: number[];
  /** the take's synced lines, for the mini lyrics strip */
  lyrics: NowLyricLine[];
}

const store = new Map<string, NowEntry>();
const subs = new Set<() => void>();

function entry(versionId: string): NowEntry {
  let e = store.get(versionId);
  if (!e) {
    e = { marks: [], lyrics: [] };
    store.set(versionId, e);
  }
  return e;
}

function emit(): void {
  for (const fn of subs) fn();
}

/** The note timestamps (seconds) for a version — the dots on the footer wave. */
export function setNoteMarks(versionId: string, marks: number[]): void {
  if (!versionId) return;
  entry(versionId).marks = marks.filter((n) => Number.isFinite(n) && n >= 0).sort((a, b) => a - b);
  emit();
}

/** The synced lyric lines for the version's take — the footer lyrics strip. */
export function setNowLyrics(versionId: string, lyrics: NowLyricLine[]): void {
  if (!versionId) return;
  entry(versionId).lyrics = lyrics;
  emit();
}

export function getNow(versionId: string | null | undefined): NowEntry {
  return (versionId && store.get(versionId)) || { marks: [], lyrics: [] };
}

export function subscribeNow(fn: () => void): () => void {
  subs.add(fn);
  return () => subs.delete(fn);
}
